import React, { useState, useEffect, useCallback } from 'react';
import { WebVitalsDashboard, type AnalyticsMetrics, analyticsApi } from '..';
import { editorApi, type SiteRecord } from '../../editor';
import { Globe, RefreshCw } from 'lucide-react';

const webbuilderApi = { ...editorApi, ...analyticsApi };

const defaultMetrics: AnalyticsMetrics = {
  pageviews30d: 0,
  uniqueVisitors30d: 0,
  conversionRatePct: 0,
  lcpP75Ms: 0,
  clsP75: 0,
  inpP75Ms: 0,
};

// AnalyticsPage composes cookieless traffic and Core Web Vitals telemetry dashboard (R-MOD-2).
export function AnalyticsPage(): React.JSX.Element {
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string>('');
  const [metrics, setMetrics] = useState<AnalyticsMetrics>(defaultMetrics);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await webbuilderApi.listSites();
        if (Array.isArray(data) && data.length > 0) {
          setSites(data);
          setActiveSiteId(data[0].id);
        }
      } catch {
        // Fallback
      }
    })();
  }, []);

  const loadAnalytics = useCallback(async (siteId: string) => {
    if (!siteId) return;
    setLoading(true);
    try {
      const data = await webbuilderApi.getAnalytics(siteId);
      if (data) {
        setMetrics({
          pageviews30d: data.pageviews_30d ?? 0,
          uniqueVisitors30d: data.unique_visitors_30d ?? 0,
          conversionRatePct: data.conversion_rate_pct ?? 0,
          lcpP75Ms: data.lcp_p75_ms ?? 0,
          clsP75: data.cls_p75 ?? 0,
          inpP75Ms: data.inp_p75_ms ?? 0,
        });
      }
    } catch {
      // Fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSiteId) {
      loadAnalytics(activeSiteId);
    }
  }, [activeSiteId, loadAnalytics]);

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4 bg-card border border-border p-4 rounded-xl">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Site:</span>
          <select
            value={activeSiteId}
            onChange={(e) => setActiveSiteId(e.target.value)}
            className="text-sm font-medium bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none"
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.subdomain_slug})
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => loadAnalytics(activeSiteId)}
          disabled={loading || !activeSiteId}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted text-xs font-medium transition-colors min-h-[38px]"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin text-primary' : ''}`} />
          <span>Refresh Metrics</span>
        </button>
      </div>

      <WebVitalsDashboard metrics={metrics} />
    </div>
  );
}

export const handle = { crumb: 'Web Vitals & Funnel Analytics' };
